/**
 * Supabase client singleton.
 *
 * Import path: '../lib/supabase.js'
 * Only imported by SERVICE files (services/tenancy.js, services/realtime.js,
 * services/pdfInserts.js) — never by components directly, and never a second
 * createClient anywhere: two GoTrue clients in one tab share the same auth
 * storage key and warn about undefined behavior.
 *
 * The anon key is safe to expose to the browser. All security-sensitive reads
 * go through SECURITY DEFINER RPC functions that run as the postgres superuser
 * internally. All write operations use SUPABASE_SERVICE_ROLE_KEY in Vercel
 * API routes — never from the browser.
 */

import { createClient } from '@supabase/supabase-js'

const url = import.meta.env.VITE_SUPABASE_URL
const key = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!url || !key) {
  // Warn loudly in dev; fail silently in prod (tenancy calls will return null)
  console.warn('[supabase] VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY is not set')
}

// realtime.eventsPerSecond: supabase-js defaults to 10 msgs/sec PER CLIENT and
// silently drops sends beyond it. The Modeler's presence traffic (pose at up to
// 20Hz while walking + cursor + doc updates + one-shot door/window 'opening'
// events) saturated that budget — peers' avatars stuttered and door swings were
// dropped before ever leaving the sender (3D presence round, 2026-08-13). 40/s
// covers the worst honest case with headroom; database/tenancy reads are
// unaffected (this only configures the realtime websocket).
export const supabase = url && key
  ? createClient(url, key, { realtime: { params: { eventsPerSecond: 40 } } })
  : null
